<template>
  <cvt-settings
    :active-tab="settingsActiveTab"
    :usage="'PopupSiteSettings'"
    @toggle="(id) => setSettingsActiveTab(id)"
  >
    <template #general>
      <div class="nav-tab-wrap">
        <!-- Popup Name Input -->
        <div class="row pb-3 mb-4">
          <div class="col-md-10">
            <cvt-input
              type="text"
              name="name"
              :floating-label="false"
              :label="'Popup Name'"
              :clearable="false"
              value="Test Popup"
            />
          </div>
        </div>

        <!-- Brand Colors -->
        <div class="row mb-4">
          <p class="col-md-10">Select Brand Colors</p>
          <div class="col-md-10">
            <colors
              :alpha="false"
              :mode="mode"
              usage="SapSiteSettings"
            ></colors>
          </div>
        </div>

        <!-- Font Family, Font Size & Font Default Text Color -->
        <cvt-collapse class="row" :accordion="true" :prepend-chevron="true">
          <cvt-collapse-item
            :title="'Typography'"
            name="properties"
            :initial-active="true"
          >
            <div
              v-for="style in fontStyles"
              :key="style.label"
              class="row flex-grow-1 mb-3"
            >
              <div
                class="col-md-3 d-flex justify-content-end align-items-center mt-3 mb-1"
              >
                <p
                  class="my-auto"
                  :style="style.format.toStyle()"
                  v-text="$t(`setup.font.${snakeCase(style.label)}`)"
                />
              </div>
              <div
                class="col-md-7 d-flex justify-content-center align-items-center"
              >
                <div class="row flex-grow-1">
                  <div class="col-7">
                    <font-selector
                      :value="style.format.font"
                      @input="updateFontFamily(style, $event)"
                    />
                  </div>
                  <div class="col-5">
                    <font-size-selector
                      :value="style.format.size"
                      @input="updateFontSize(style, $event)"
                    />
                  </div>
                </div>
              </div>
              <div
                class="col-md-2 d-flex justify-content-center align-items-center mt-3 mb-1"
              >
                <div class="row">
                  <div class="col-12">
                    <default-color-selector
                      :value="style.format.defaultTextColor"
                      @input="updateDefaultTextColor(style, $event)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </cvt-collapse-item>
        </cvt-collapse>

        <!-- Button Default Styles -->
        <cvt-collapse class="row" :accordion="true" :prepend-chevron="true">
          <cvt-collapse-item
            :title="'Buttons'"
            name="properties"
            :initial-active="false"
          >
            <div
              v-for="style in buttonDefaultStyles"
              :key="style.label"
              class="row flex-grow-1"
            >
              <div
                class="col-md-3 d-flex justify-content-end align-items-center mt-3 mb-1"
              >
                <div class="c-btn-wrapper">
                  <c-button class="my-auto" :cta="style.label" />
                </div>
              </div>
              <div
                class="col-md-7 d-flex justify-content-center align-items-center"
              >
                <div class="row flex-grow-1">
                  <div class="col-7">
                    <font-selector
                      :value="style.format.fontFamily"
                      @input="updateButtonDefaultFontFamily(style, $event)"
                    />
                  </div>
                  <div class="col-5">
                    <font-size-selector
                      :value="style.format.fontSize"
                      @input="updateButtonDefaultFontSize(style, $event)"
                    />
                  </div>
                </div>
              </div>
              <div
                class="col-md-2 d-flex justify-content-center align-items-center mt-3 mb-1"
              >
                <div class="row">
                  <div class="col-12">
                    <default-color-selector
                      :value="style.format.defaultBackgroundColor"
                      @input="updateButtonDefaultBackgroundColor(style, $event)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </cvt-collapse-item>
        </cvt-collapse>

        <!-- Link Default Styles -->
        <cvt-collapse class="row" :accordion="true" :prepend-chevron="true">
          <cvt-collapse-item
            :title="'Links'"
            name="properties"
            :initial-active="false"
          >
            <div
              v-for="style in linkDefaultStyles"
              :key="style.label"
              class="row flex-grow-1"
            >
              <div
                class="col-md-3 d-flex justify-content-end align-items-center mt-1 mb-2"
              >
                <div class="c-btn-wrapper">
                  <a
                    class="my-auto underline-link"
                    :style="style.format.getLinkFontStyle()"
                  >
                    {{ style.label }}
                  </a>
                </div>
              </div>
              <div class="col-md-2 d-flex align-items-center m-0">
                <div class="row">
                  <div class="col-12">
                    <default-color-selector
                      :value="style.format.defaultTextColor"
                      @input="updateFroalaLinkDefaultTextColor(style, $event)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </cvt-collapse-item>
        </cvt-collapse>
      </div>
    </template>

    <template #audience>
      <div class="nav-tab-wrap">
        <cvt-sap-audience />
      </div>
    </template>

    <template #rules>
      <div class="nav-tab-wrap">
        <cvt-sap-rules />
      </div>
    </template>

  </cvt-settings>
</template>

<script>
import * as getters from "../../../store/modules/getters";
import * as mutations from "../../../store/modules/mutations";
import * as actions from "../../../store/modules/actions";
import { mapGetters, mapMutations, mapActions, mapState } from 'vuex';
import Colors from '../common/Colors.vue';
import FontSelector from '../../../components/common/FontSelector.vue';
import FontSizeSelector from '../../../components/common/FontSizeSelector.vue';
import DefaultColorSelector from '../../../components/common/DefaultColorSelector.vue';

export default {
  components: {
    Colors,
    FontSelector,
    FontSizeSelector,
    DefaultColorSelector,
  },
  props: {
    mode: {
      type: String,
      default: 'LIGHT',
    },
  },
  data() {
    return {};
  },

  computed: {
    ...mapGetters({
      siteSettings: getters.SITE_SETTINGS,
      fontStyles: getters.THEME_FONT_STYLES,
      buttonDefaultStyles: getters.THEME_BUTTON_DEFAULT_STYLES,
      linkDefaultStyles: getters.THEME_FROALA_LINK_DEFAULT_STYLES,
      settingsActiveTab: getters.SITE_SETTINGS_ACTIVE_TAB,
    }),
  },

  methods: {
    ...mapMutations({
      setSettingsActiveTab: mutations.SITE_TRIGGER_TAB,
    }),
    // ...mapActions({
    //   toggleSiteSettingsVisibilty: sapActions.SAP_SET_SITE_SETTINGS_VISIBILITY,
    //   setSiteSettingsActiveTab: sapActions.SAP_SET_SITE_SETTINGS_ACTIVE_TAB,
    // }),

    // TODO: uncomment and config it when VUEX structure is finalize
    // updateFontFamily({ style, format }, font) {
    //   this.setFont({
    //     style,
    //     format: { ...format, font },
    //   });
    // },

    // TODO: uncomment and config it when VUEX structure is finalize
    // updateFontSize({ style, format }, size) {
    //   this.setFont({
    //     style,
    //     format: { ...format, size },
    //   });
    // },

    // TODO: uncomment and config it when VUEX structure is finalize
    // updateDefaultTextColor({ style, format }, defaultTextColor) {
    //   this.setFont({
    //     style,
    //     format: { ...format, defaultTextColor },
    //   });
    // },

    // TODO: uncomment and config it when VUEX structure is finalize
    // updateButtonDefaultFontFamily({ item, format }, fontFamily) {
    //   this.setButtonDefaultStyles({
    //     item,
    //     format: { ...format, fontFamily },
    //   });
    // },

    // TODO: uncomment and config it when VUEX structure is finalize
    // updateButtonDefaultFontSize({ item, format }, fontSize) {
    //   this.setButtonDefaultStyles({
    //     item,
    //     format: { ...format, fontSize },
    //   });
    // },

    // TODO: uncomment and config it when VUEX structure is finalize
    // updateButtonDefaultBackgroundColor(
    //   { item, format },
    //   defaultBackgroundColor
    // ) {
    //   this.setButtonDefaultStyles({
    //     item,
    //     format: { ...format, defaultBackgroundColor },
    //   });
    // },

    // TODO: uncomment and config it when VUEX structure is finalize
    // updateFroalaLinkDefaultTextColor({ item, format }, defaultTextColor) {
    //   this.setFroalaLinkDefaultStyles({
    //     item,
    //     format: { ...format, defaultTextColor },
    //   });
    // },

  },
};
</script>

<style scoped>
.nav-tab-wrap {
  padding: 0px 20px 0px 20px;
}
.c-btn-wrapper {
  pointer-events: none;
}
.underline-link {
  text-decoration: underline;
}
</style>
